/**
 * Cliente HTTP de Nucleo ERP.
 *
 * Toda la app habla con el backend por aca. Tres decisiones que conviene conocer
 * antes de tocar este archivo:
 *
 * 1. **Los tokens viven en este modulo**, no en el store de Zustand. El store los
 *    refleja para que la UI reaccione, pero la fuente de verdad es esta, porque
 *    el refresco puede dispararse desde una peticion en vuelo sin que haya ningun
 *    componente montado.
 * 2. **El refresco es de un solo vuelo.** Si diez peticiones reciben 401 a la vez,
 *    se emite UN refresh y las diez esperan el mismo resultado. Sin esto, una
 *    pantalla con varias consultas invalida su propio refresh token en cascada.
 * 3. **La company activa viaja en la cabecera `X-Company-Id`**, nunca en la URL.
 *    El backend la lee en `TenantMiddleware`; el tenant NO se elige desde el
 *    cliente: sale del usuario autenticado.
 */

import type {
  ActionResult,
  ActivityBoard,
  Attachment,
  AttachmentList,
  ChatterActivity,
  ChatterMessage,
  ChatterThread,
  Domain,
  ExportOptions,
  ExportPreview,
  ImportOptions,
  ImportPreview,
  ImportResult,
  JsonValue,
  ListParams,
  ListResponse,
  MailPreview,
  MailSendPayload,
  MailSendResult,
  MassMailingPreview,
  MassMailingResult,
  MenuNode,
  MenusResponse,
  ModelSpec,
  ReadGroupParams,
  ReadGroupResponse,
  RecordData,
  RecordPayload,
  Session,
  TokenPair,
} from '@/lib/types';

const API_BASE = '/api/v1';
const STORAGE_KEY = 'nucleo.auth';
const COMPANY_HEADER = 'X-Company-Id';

// ---------------------------------------------------------------------------
// Errores
// ---------------------------------------------------------------------------
/**
 * Error de API con el detalle ya interpretado.
 *
 * `fields` trae los errores de validacion de DRF (`{campo: [mensajes]}`) para que
 * un formulario los pueda pintar campo por campo en vez de tirar un toast generico.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly fields: Record<string, string[]>;
  /**
   * `{campo tecnico: etiqueta visible}` que manda el backend junto con el 400.
   *
   * Existe para que el mensaje se pueda escribir en castellano sin que el
   * cliente tenga que conocer el modelo: las CLAVES de `fields` siguen siendo
   * tecnicas porque el formulario las necesita para marcar el control y darle
   * el foco, pero al usuario ya no se le muestra ninguna.
   */
  readonly fieldLabels: Record<string, string>;
  readonly payload: unknown;

  constructor(
    status: number,
    message: string,
    fields: Record<string, string[]> = {},
    payload: unknown = null,
    fieldLabels: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.fields = fields;
    this.fieldLabels = fieldLabels;
    this.payload = payload;
  }

  /** Etiqueta visible de un campo; el nombre tecnico solo si el backend no mando una. */
  labelOf(field: string): string {
    return this.fieldLabels[field] ?? field;
  }

  /** `true` cuando el problema es de credenciales y no de datos. */
  get isAuthError(): boolean {
    return this.status === 401;
  }

  get isPermissionError(): boolean {
    return this.status === 403;
  }
}

/**
 * Largo maximo de un cuerpo de texto plano que todavia puede pasar por mensaje.
 *
 * Cualquier cosa mas larga que esto no es un mensaje: es una pagina.
 */
const LARGO_MAXIMO_MENSAJE = 300;

/** Traduce el cuerpo de un error de DRF a mensaje + errores por campo. */
function parseErrorBody(status: number, body: unknown): ApiError {
  if (typeof body === 'string' && body.trim() !== '') {
    // Un cuerpo que no es JSON casi nunca es un mensaje para el usuario. Con
    // DEBUG=1 un 500 devuelve la pagina de depuracion de Django —23 kB con el
    // traceback, el PYTHONPATH, los INSTALLED_APPS y el middleware— y esto la
    // usaba TAL CUAL como texto del error: la pantalla escupia la configuracion
    // del servidor en vez de decir que fallo. Se acepta solo si es corto y no
    // parece HTML; si no, manda el mensaje por estado. El cuerpo entero sigue
    // estando en `payload` para quien lo quiera inspeccionar.
    const texto = body.trim();
    const pareceHtml = /^\s*(<!doctype|<html|<\?xml)/i.test(texto) || texto.includes('</');
    if (!pareceHtml && texto.length <= LARGO_MAXIMO_MENSAJE) {
      return new ApiError(status, texto, {}, body);
    }
    return new ApiError(status, mensajePorEstado(status), {}, body);
  }

  if (body !== null && typeof body === 'object') {
    const data = body as Record<string, unknown>;

    const detail = data.detail;
    if (typeof detail === 'string') {
      return new ApiError(status, detail, {}, body);
    }

    // `field_labels` no es un error: es el diccionario `{campo: etiqueta}` que
    // el backend adjunta a cada 400 de validacion para que el aviso se pueda
    // escribir con el nombre que el usuario ve en el formulario. Sin esto la
    // administradora leia «due_date_2: El segundo vencimiento…» o, peor,
    // «__all__: El periodo ya fue emitido…».
    const fieldLabels = leerEtiquetas(data[CLAVE_ETIQUETAS]);

    const fields: Record<string, string[]> = {};
    for (const [clave, valor] of Object.entries(data)) {
      if (clave === CLAVE_ETIQUETAS) continue;
      if (Array.isArray(valor)) {
        fields[clave] = valor.map((v) => String(v));
      } else if (typeof valor === 'string') {
        fields[clave] = [valor];
      }
    }

    const primera = Object.entries(fields)[0];
    if (primera !== undefined) {
      const [campo, mensajes] = primera;
      const texto = mensajes[0] ?? 'Dato inválido.';
      const mensaje = esErrorGeneral(campo) ? texto : `${fieldLabels[campo] ?? campo}: ${texto}`;
      return new ApiError(status, mensaje, fields, body, fieldLabels);
    }
  }

  return new ApiError(status, mensajePorEstado(status), {}, body);
}

/** Clave con la que el backend manda `{campo: etiqueta}` en un 400 de validacion. */
const CLAVE_ETIQUETAS = 'field_labels';

/**
 * Claves que NO nombran un campo: el error es del registro entero.
 *
 * `__all__` es la de Django y `non_field_errors` la de DRF. El backend ya
 * normaliza a la segunda, pero se aceptan las dos: un endpoint viejo —o un
 * `ValidationError` que no pase por `core/api.py`— no puede terminar
 * imprimiendole `__all__` al usuario.
 */
function esErrorGeneral(campo: string): boolean {
  return campo === 'non_field_errors' || campo === '__all__' || campo === 'detail';
}

/** Lee `field_labels` con desconfianza: cualquier cosa que no sea texto se descarta. */
function leerEtiquetas(crudo: unknown): Record<string, string> {
  if (crudo === null || typeof crudo !== 'object' || Array.isArray(crudo)) return {};
  const salida: Record<string, string> = {};
  for (const [campo, etiqueta] of Object.entries(crudo as Record<string, unknown>)) {
    if (typeof etiqueta === 'string' && etiqueta !== '') salida[campo] = etiqueta;
  }
  return salida;
}

function mensajePorEstado(status: number): string {
  switch (status) {
    case 400:
      return 'La solicitud tiene datos inválidos.';
    case 401:
      return 'Tu sesión expiró. Volvé a iniciar sesión.';
    case 403:
      return 'No tenés permisos para esta operación.';
    case 404:
      return 'No se encontró lo que buscabas.';
    // 409 es «el estado actual no lo permite»: hay algo que depende de este
    // registro, o ya se emitió. Repetirlo da lo mismo, así que el texto NO
    // invita a reintentar. Casi siempre el backend manda además el motivo
    // escrito en `detail` y esto no llega a usarse; cuando no lo manda, al
    // menos no se le miente a la persona.
    case 409:
      return 'No se puede hacer por el estado actual del registro.';
    case 429:
      return 'Demasiadas solicitudes. Esperá unos segundos.';
    case 500:
    case 502:
    case 503:
      return 'El servidor tuvo un problema. Intentá de nuevo en un momento.';
    default:
      return `Error inesperado (HTTP ${status}).`;
  }
}

// ---------------------------------------------------------------------------
// Estado de autenticacion
// ---------------------------------------------------------------------------
export interface AuthState {
  access: string | null;
  refresh: string | null;
  /** Company activa elegida por el usuario. `null` = la que decida el backend. */
  companyId: number | null;
}

const ESTADO_VACIO: AuthState = { access: null, refresh: null, companyId: null };

function leerAlmacenado(): AuthState {
  if (typeof localStorage === 'undefined') return { ...ESTADO_VACIO };
  try {
    const crudo = localStorage.getItem(STORAGE_KEY);
    if (crudo === null) return { ...ESTADO_VACIO };
    const datos = JSON.parse(crudo) as Partial<AuthState>;
    return {
      access: typeof datos.access === 'string' ? datos.access : null,
      refresh: typeof datos.refresh === 'string' ? datos.refresh : null,
      companyId: typeof datos.companyId === 'number' ? datos.companyId : null,
    };
  } catch {
    // JSON corrupto o storage bloqueado: se arranca sin sesion.
    return { ...ESTADO_VACIO };
  }
}

let auth: AuthState = leerAlmacenado();
const oyentes = new Set<(estado: AuthState) => void>();

function persistir(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (auth.access === null && auth.refresh === null && auth.companyId === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
    }
  } catch {
    // Storage no disponible: la sesion vive solo en memoria.
  }
}

function actualizarAuth(parcial: Partial<AuthState>): void {
  auth = { ...auth, ...parcial };
  persistir();
  for (const oyente of oyentes) oyente(auth);
}

export function getAuthState(): AuthState {
  return auth;
}

/** Suscribe a los cambios de token/company. Devuelve la funcion para desuscribirse. */
export function subscribeAuth(oyente: (estado: AuthState) => void): () => void {
  oyentes.add(oyente);
  return () => {
    oyentes.delete(oyente);
  };
}

export function isAuthenticated(): boolean {
  return auth.access !== null;
}

/**
 * El id del usuario dueño del token guardado, o `null`.
 *
 * Se lee del token y no de la sesión porque hace falta ANTES de que la sesión
 * vuelva: la metadata que se guarda en el navegador (ver
 * `components/shell/queries.ts`) es por usuario, y en un F5 se usa mientras
 * `/session/` todavía está en vuelo. No valida nada: la firma la verifica el
 * servidor en cada pedido. Un token ilegible da `null` y simplemente no se usa
 * lo guardado.
 */
export function usuarioDelToken(): string | null {
  const partes = auth.access?.split('.') ?? [];
  if (partes.length !== 3 || partes[1] === undefined) return null;
  try {
    const json = atob(partes[1].replace(/-/g, '+').replace(/_/g, '/'));
    const datos = JSON.parse(json) as { user_id?: unknown };
    return datos.user_id === undefined || datos.user_id === null ? null : String(datos.user_id);
  } catch {
    return null;
  }
}

export function setActiveCompanyId(companyId: number | null): void {
  actualizarAuth({ companyId });
}

export function getActiveCompanyId(): number | null {
  return auth.companyId;
}

/** Borra la sesion local. No llama al backend: los JWT no se invalidan del lado del cliente. */
export function clearSession(): void {
  actualizarAuth({ access: null, refresh: null, companyId: null });
}

// ---------------------------------------------------------------------------
// Sesion expirada
// ---------------------------------------------------------------------------
const oyentesExpiracion = new Set<() => void>();

/** Avisa cuando el refresh fallo y hay que volver al login. */
export function onSessionExpired(oyente: () => void): () => void {
  oyentesExpiracion.add(oyente);
  return () => {
    oyentesExpiracion.delete(oyente);
  };
}

function notificarExpiracion(): void {
  clearSession();
  for (const oyente of oyentesExpiracion) oyente();
}

// ---------------------------------------------------------------------------
// Motor de peticiones
// ---------------------------------------------------------------------------
type QueryValue = string | number | boolean | undefined | null;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  /** `false` en login y refresh: no llevan Authorization ni disparan reintento. */
  authenticated?: boolean;
  signal?: AbortSignal;
}

function construirUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = `${API_BASE}${path}`;
  if (query === undefined) return url;

  const params = new URLSearchParams();
  for (const [clave, valor] of Object.entries(query)) {
    if (valor === undefined || valor === null || valor === '') continue;
    params.set(clave, String(valor));
  }
  const cadena = params.toString();
  return cadena === '' ? url : `${url}?${cadena}`;
}

async function leerCuerpo(respuesta: Response): Promise<unknown> {
  if (respuesta.status === 204) return null;
  const tipo = respuesta.headers.get('content-type') ?? '';
  if (tipo.includes('application/json')) {
    try {
      return (await respuesta.json()) as unknown;
    } catch {
      return null;
    }
  }
  const texto = await respuesta.text();
  return texto === '' ? null : texto;
}

let refrescoEnCurso: Promise<string> | null = null;

/**
 * Renueva el access token. Si ya hay un refresco en vuelo devuelve esa misma
 * promesa: el refresh token es de un solo uso y dispararlo dos veces en paralelo
 * deja la sesion rota.
 */
async function refrescarToken(): Promise<string> {
  if (refrescoEnCurso !== null) return refrescoEnCurso;

  const tokenRefresh = auth.refresh;
  if (tokenRefresh === null) {
    notificarExpiracion();
    throw new ApiError(401, 'No hay sesión activa.');
  }

  refrescoEnCurso = (async () => {
    const respuesta = await fetch(`${API_BASE}/auth/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refresh: tokenRefresh }),
    });

    if (!respuesta.ok) {
      notificarExpiracion();
      throw parseErrorBody(respuesta.status, await leerCuerpo(respuesta));
    }

    const datos = (await respuesta.json()) as { access?: string; refresh?: string };
    if (typeof datos.access !== 'string') {
      notificarExpiracion();
      throw new ApiError(401, 'El servidor no devolvió un token válido.');
    }

    // Con ROTATE_REFRESH_TOKENS el backend manda uno nuevo; si no, se conserva.
    actualizarAuth({
      access: datos.access,
      refresh: typeof datos.refresh === 'string' ? datos.refresh : auth.refresh,
    });
    return datos.access;
  })();

  try {
    return await refrescoEnCurso;
  } finally {
    refrescoEnCurso = null;
  }
}

async function ejecutar(path: string, opciones: RequestOptions, token: string | null): Promise<Response> {
  const cabeceras: Record<string, string> = { Accept: 'application/json' };

  if (opciones.body !== undefined) {
    cabeceras['Content-Type'] = 'application/json';
  }
  if (token !== null) {
    cabeceras.Authorization = `Bearer ${token}`;
  }
  if (auth.companyId !== null) {
    cabeceras[COMPANY_HEADER] = String(auth.companyId);
  }

  const init: RequestInit = {
    method: opciones.method ?? 'GET',
    headers: cabeceras,
    credentials: 'same-origin',
  };
  if (opciones.body !== undefined) {
    init.body = JSON.stringify(opciones.body);
  }
  if (opciones.signal !== undefined) {
    init.signal = opciones.signal;
  }

  return fetch(construirUrl(path, opciones.query), init);
}

async function request<T>(path: string, opciones: RequestOptions = {}): Promise<T> {
  const requiereAuth = opciones.authenticated !== false;

  let respuesta: Response;
  try {
    respuesta = await ejecutar(path, opciones, requiereAuth ? auth.access : null);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'No se pudo contactar al servidor. Revisá tu conexion.', {}, error);
  }

  // 401 con sesion: un intento de refresco y se repite la peticion original.
  if (respuesta.status === 401 && requiereAuth && auth.refresh !== null) {
    const nuevo = await refrescarToken();
    try {
      respuesta = await ejecutar(path, opciones, nuevo);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new ApiError(0, 'No se pudo contactar al servidor. Revisá tu conexion.', {}, error);
    }
  }

  if (!respuesta.ok) {
    const cuerpo = await leerCuerpo(respuesta);
    if (respuesta.status === 401 && requiereAuth) {
      notificarExpiracion();
    }
    throw parseErrorBody(respuesta.status, cuerpo);
  }

  return (await leerCuerpo(respuesta)) as T;
}

// ---------------------------------------------------------------------------
// Autenticacion
// ---------------------------------------------------------------------------
/**
 * Obtiene el par de tokens y los deja activos.
 *
 * El campo del cuerpo es `email`, no `username`: el modelo de usuario del
 * backend (`base.User`) declara `USERNAME_FIELD = "email"`, y simplejwt arma el
 * serializer del login con ese nombre. Mandar `username` devuelve un 400
 * diciendo que falta `email`.
 */
export async function login(email: string, password: string): Promise<TokenPair> {
  const tokens = await request<TokenPair>('/auth/token/', {
    method: 'POST',
    body: { email, password },
    authenticated: false,
  });
  actualizarAuth({ access: tokens.access, refresh: tokens.refresh });
  return tokens;
}

/**
 * Adopta un par de tokens que emitio OTRO endpoint y deja la sesion activa.
 *
 * Existe por la pantalla de activacion: el boton dice «Guardar y entrar» y
 * `POST /acceso/activar/` devuelve el par de tokens junto con el email, asi que
 * la persona queda adentro sin volver a tipear la contrasena que acaba de
 * elegir. Sin esto habria que reimplementar `login()` con la clave en la mano —
 * un segundo camino de autenticacion en el cliente— o mandarla al ingreso, que
 * es exactamente el rodeo que este endpoint viene a sacar.
 *
 * A partir de acá los tokens son indistinguibles de los de un ingreso normal:
 * el refresco, la rotacion y el corte de credenciales se comportan igual.
 */
export function adoptSession(access: string, refresh: string): void {
  actualizarAuth({ access, refresh });
}

/** Cierra la sesion en el cliente. */
export function logout(): void {
  clearSession();
}

// ---------------------------------------------------------------------------
// Endpoints del nucleo
// ---------------------------------------------------------------------------
export function fetchSession(signal?: AbortSignal): Promise<Session> {
  return request<Session>('/session/', signal !== undefined ? { signal } : {});
}

export async function fetchMenus(signal?: AbortSignal): Promise<MenuNode[]> {
  const datos = await request<MenusResponse>('/menus/', signal !== undefined ? { signal } : {});
  return datos.menus;
}

/**
 * Metadata de un modelo. Es la llamada que conviene cachear con mas agresividad.
 *
 * Con `recordId` el backend agrega la evaluacion POR REGISTRO de las acciones
 * (`?id=<pk>`): sin el, `enabled` solo puede reflejar permisos y grupos, y el
 * formulario de un periodo ya cerrado mostraria «Liquidar» habilitado. La
 * variante sin id es la que necesita —y cachea— la vista de lista.
 */
export function fetchMeta(
  modelName: string,
  recordId?: number | null,
  signal?: AbortSignal,
): Promise<ModelSpec> {
  const ruta = `/meta/${encodeURIComponent(modelName)}/${
    recordId === undefined || recordId === null ? '' : `?id=${recordId}`
  }`;
  return request<ModelSpec>(ruta, signal !== undefined ? { signal } : {});
}

/** Lo que cambia por registro de cada botón: si se puede apretar y por qué no. */
export interface EstadoDeBoton {
  name: string;
  enabled: boolean;
  disabled_reason: string;
}

/** Lo que cambia por registro en la metadata: el estado de cada botón. */
export interface MetaDeRegistro {
  actions: EstadoDeBoton[];
  reports: EstadoDeBoton[];
  mailing: ModelSpec['mailing'];
}

/**
 * `GET /meta/<modelo>/acciones/?id=<pk>`: sólo `actions`, `reports` y `mailing`
 * evaluados contra ese registro. Es lo que se superpone a la metadata del
 * modelo —cacheada largo— al abrir una ficha, en vez de volver a bajarla entera.
 */
export function fetchMetaAcciones(
  modelName: string,
  recordId: number,
  signal?: AbortSignal,
): Promise<MetaDeRegistro> {
  const ruta = `/meta/${encodeURIComponent(modelName)}/acciones/?id=${recordId}`;
  return request<MetaDeRegistro>(ruta, signal !== undefined ? { signal } : {});
}

function serializarDominio(domain: Domain | undefined): string | undefined {
  if (domain === undefined || domain.length === 0) return undefined;
  return JSON.stringify(domain);
}

/** Lista registros. Sin `limit` el backend usa 80 y topea en 500. */
export function searchRead<T = RecordData>(
  modelName: string,
  params: ListParams = {},
  signal?: AbortSignal,
): Promise<ListResponse<T>> {
  const query: Record<string, QueryValue> = {
    domain: serializarDominio(params.domain),
    order: params.order,
    limit: params.limit,
    offset: params.offset,
    fields: params.fields === undefined ? undefined : params.fields.join(','),
    include_inactive: params.include_inactive === true ? 1 : undefined,
  };
  const opciones: RequestOptions = { query };
  if (signal !== undefined) opciones.signal = signal;
  return request<ListResponse<T>>(`/model/${encodeURIComponent(modelName)}/`, opciones);
}

/** Una consulta de `contar`: qué lista contar y si también hacen falta sus totales. */
export interface ConsultaDeConteo {
  model: string;
  domain?: Domain;
  totales?: boolean;
}

/** Lo que vuelve por cada consulta: el número, o el error de ESA consulta. */
export interface ResultadoDeConteo {
  count?: number;
  totals?: Record<string, string | number>;
  error?: number;
  detail?: string;
}

/**
 * `POST /contar/`: varios conteos de listas en UN pedido (ver `ContarView`).
 *
 * Cada número es el que daría `searchRead(...).count` con el mismo dominio —el
 * servidor lo cuenta con el mismo filtro que la lista—, pero sin pagar un
 * pedido entero por cada uno. Un modelo que el usuario no puede leer contesta
 * su error en su lugar sin tirar abajo los demás.
 */
export function contar(consultas: ConsultaDeConteo[], signal?: AbortSignal): Promise<ResultadoDeConteo[]> {
  const cuerpo = consultas.map((c) => ({ model: c.model, domain: c.domain ?? [], totales: c.totales === true }));
  const opciones: RequestOptions = { method: 'POST', body: { consultas: cuerpo } };
  if (signal !== undefined) opciones.signal = signal;
  return request<{ resultados: ResultadoDeConteo[] }>('/contar/', opciones).then((r) => r.resultados);
}

export function readRecord<T = RecordData>(
  modelName: string,
  id: number,
  fields?: string[],
  signal?: AbortSignal,
): Promise<T> {
  const opciones: RequestOptions = {
    query: { fields: fields === undefined ? undefined : fields.join(',') },
  };
  if (signal !== undefined) opciones.signal = signal;
  return request<T>(`/model/${encodeURIComponent(modelName)}/${id}/`, opciones);
}

/**
 * `fields` vale lo mismo que en `readRecord`, y por lo mismo: la respuesta del
 * guardado es el registro releído, y sin acotarla trae las relaciones inversas
 * enteras —una consulta del servidor por cada una— que el formulario no mira.
 */
export function createRecord<T = RecordData>(
  modelName: string,
  values: RecordPayload,
  fields?: string[],
): Promise<T> {
  return request<T>(`/model/${encodeURIComponent(modelName)}/`, {
    method: 'POST',
    body: values,
    query: { fields: fields === undefined ? undefined : fields.join(',') },
  });
}

export function updateRecord<T = RecordData>(
  modelName: string,
  id: number,
  values: RecordPayload,
  fields?: string[],
): Promise<T> {
  return request<T>(`/model/${encodeURIComponent(modelName)}/${id}/`, {
    method: 'PATCH',
    body: values,
    query: { fields: fields === undefined ? undefined : fields.join(',') },
  });
}

/**
 * Borrado real. La UI archiva (`active: false`) salvo que borrar sea lo correcto:
 * en un ERP contable el borrado rompe trazabilidad.
 */
export function deleteRecord(modelName: string, id: number): Promise<null> {
  return request<null>(`/model/${encodeURIComponent(modelName)}/${id}/`, { method: 'DELETE' });
}

/** Archiva un registro sin borrarlo. */
export function archiveRecord<T = RecordData>(
  modelName: string,
  id: number,
  archived = true,
  fields?: string[],
): Promise<T> {
  return updateRecord<T>(modelName, id, { active: !archived }, fields);
}

/**
 * El cuadro de una tarjeta de la ficha: lo que cuenta, sin salir de ella.
 *
 * `GET /api/v1/model/<modelo>/<id>/boton/<nombre>/`. Devuelve `{table, target}`:
 * el cuadro con las filas que cuenta la tarjeta y el destino de «Abrir la lista
 * completa». Es para las tarjetas sin cuadro propio (sin `accion`); las que lo
 * tienen siguen corriendo su accion.
 */
export function smartButtonTable(
  modelName: string,
  id: number,
  buttonName: string,
): Promise<{ table?: unknown }> {
  return request<{ table?: unknown }>(
    `/model/${encodeURIComponent(modelName)}/${id}/boton/${encodeURIComponent(buttonName)}/`,
  );
}

/**
 * Ejecuta una accion de servidor sobre un registro.
 *
 * `POST /api/v1/model/<modelo>/<id>/action/<nombre>/`. La logica vive entera en
 * el backend: el cliente manda el disparo y refresca. No hay ninguna regla de
 * negocio de este lado, y no puede haberla — un boton que decide si se puede
 * liquidar un periodo seria una segunda maquina de estados que se desincroniza
 * con la de verdad.
 */
export function callAction<T = ActionResult>(
  modelName: string,
  id: number,
  actionName: string,
  params: Record<string, JsonValue> = {},
): Promise<T> {
  // Los parametros van ANIDADOS bajo `params`: el backend los lee con
  // `request.data.get("params")` y los valida con `ActionParam.coerce()`.
  // Mandarlos sueltos en la raiz del cuerpo es mandarlos a ningun lado.
  return request<T>(
    `/model/${encodeURIComponent(modelName)}/${id}/action/${encodeURIComponent(actionName)}/`,
    { method: 'POST', body: { params } },
  );
}

/**
 * Ejecuta una accion sobre una seleccion entera.
 *
 * `POST /api/v1/model/<modelo>/action/<nombre>/` con `{"ids": [...]}`. Es todo o
 * nada: el backend envuelve la seleccion completa en una transaccion, asi que si
 * el tercer registro no se puede procesar los dos primeros tampoco quedan
 * hechos. Una accion `target="selection"` la recibe de una sola vez, que es lo
 * que su servicio espera.
 */
export function callCollectionAction<T = ActionResult>(
  modelName: string,
  actionName: string,
  ids: number[],
  params: Record<string, JsonValue> = {},
): Promise<T> {
  return request<T>(`/model/${encodeURIComponent(modelName)}/action/${encodeURIComponent(actionName)}/`, {
    method: 'POST',
    body: { ids, params },
  });
}

/** Un archivo descargado del backend: sus bytes y el nombre que trae la cabecera. */
export interface DownloadedFile {
  blob: Blob;
  filename: string;
  /** Sólo en exportaciones: cuántas filas trae el archivo. `0` en el resto. */
  rowCount?: number;
  /** Sólo en exportaciones: `true` si el tope del servidor cortó el listado. */
  truncated?: boolean;
}

/**
 * Lee el nombre del archivo del `Content-Disposition`.
 *
 * Prefiere `filename*` (RFC 5987, con acentos y espacios) sobre el `filename`
 * simple. El backend manda los dos; sin esta cabecera se cae a un nombre por
 * defecto, porque una descarga sin nombre queda como un UUID en la carpeta.
 */
function nombreDeContentDisposition(header: string | null, porDefecto: string): string {
  if (header === null) return porDefecto;
  const estrella = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (estrella?.[1] !== undefined) {
    try {
      return decodeURIComponent(estrella[1]);
    } catch {
      // Cabecera mal formada: seguimos con el filename simple.
    }
  }
  const simple = /filename="?([^"]+)"?/i.exec(header);
  return simple?.[1] ?? porDefecto;
}

/**
 * Descarga un archivo binario (un PDF de reporte) con reintento de refresco.
 *
 * No pasa por `request<T>` porque ese helper interpreta el cuerpo como JSON o
 * texto; un PDF es binario y hay que leerlo como `Blob`. La cadena de auth
 * —cabecera de company, 401 y un único refresco— es la misma que la de todas las
 * peticiones, reusando `ejecutar` y `refrescarToken`.
 */
async function descargarArchivo(
  path: string,
  porDefecto: string,
  opciones: RequestOptions = {},
): Promise<DownloadedFile> {
  let respuesta: Response;
  try {
    respuesta = await ejecutar(path, opciones, auth.access);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'No se pudo contactar al servidor. Revisá tu conexion.', {}, error);
  }

  if (respuesta.status === 401 && auth.refresh !== null) {
    const nuevo = await refrescarToken();
    respuesta = await ejecutar(path, opciones, nuevo);
  }

  if (!respuesta.ok) {
    const cuerpo = await leerCuerpo(respuesta);
    if (respuesta.status === 401) notificarExpiracion();
    throw parseErrorBody(respuesta.status, cuerpo);
  }

  const blob = await respuesta.blob();
  const filename = nombreDeContentDisposition(respuesta.headers.get('content-disposition'), porDefecto);
  return {
    blob,
    filename,
    // El backend cuenta lo que salio en cabeceras y no en el cuerpo, porque el
    // cuerpo ES el archivo: una descarga no tiene donde meter un JSON.
    rowCount: Number(respuesta.headers.get('x-nucleo-export-count') ?? '0'),
    truncated: respuesta.headers.get('x-nucleo-export-truncated') === '1',
  };
}

/**
 * Genera y descarga el PDF de un reporte de un registro.
 *
 * `GET /api/v1/model/<modelo>/<id>/print/<reporte>/`. El backend verifica el
 * permiso de lectura y las reglas de registro antes de componer el PDF, así que
 * la UI sólo dispara la descarga y no reimplementa ningún control.
 */
export function downloadReport(
  modelName: string,
  id: number,
  reportName: string,
  params: Record<string, JsonValue> = {},
): Promise<DownloadedFile> {
  // Los parametros declarados del reporte —el rango de fechas de un libro de
  // IVA, la cuenta de un mayor— viajan en la query string porque imprimir es un
  // GET: asi la URL del PDF se puede pegar en el navegador o mandar por mail y
  // sigue dando el mismo papel. El backend los valida con el mismo `coerce()`
  // que usa en una accion de servidor.
  const query = new URLSearchParams();
  for (const [clave, valor] of Object.entries(params)) {
    if (valor === null || valor === undefined || valor === '') continue;
    query.set(clave, typeof valor === 'boolean' ? String(valor) : String(valor));
  }
  const sufijo = query.toString() === '' ? '' : `?${query.toString()}`;
  const ruta = `/model/${encodeURIComponent(modelName)}/${id}/print/${encodeURIComponent(reportName)}/${sufijo}`;
  return descargarArchivo(ruta, `${reportName}.pdf`);
}

// ---------------------------------------------------------------------------
// Correo saliente
// ---------------------------------------------------------------------------
/** Ruta base del envío individual de un registro. */
function rutaMail(modelName: string, id: number): string {
  return `/model/${encodeURIComponent(modelName)}/${id}/mail/`;
}

/** Ruta base de un envío masivo declarado sobre un registro. */
function rutaMasivo(modelName: string, id: number, mailing: string): string {
  return `/model/${encodeURIComponent(modelName)}/${id}/mailing/${encodeURIComponent(mailing)}/`;
}

/**
 * La propuesta de mail de un registro: destinatario, asunto, cuerpo y adjunto.
 *
 * Trae ademas la `request_key`, que es la clave de idempotencia: se devuelve tal
 * cual en el POST y es lo que hace que un doble click no mande dos veces. NO se
 * genera en el cliente a proposito — la emite el servidor, que es quien despues
 * la va a hacer cumplir contra su base.
 */
export function mailPreview(modelName: string, id: number, signal?: AbortSignal): Promise<MailPreview> {
  return request<MailPreview>(rutaMail(modelName, id), signal !== undefined ? { signal } : {});
}

/** Manda el mail con lo que el usuario dejo en el dialogo. */
export function mailSend(
  modelName: string,
  id: number,
  payload: MailSendPayload,
): Promise<MailSendResult> {
  return request<MailSendResult>(rutaMail(modelName, id), { method: 'POST', body: payload });
}

/** Vista previa del masivo: a quienes, cuantos sin email y que va a decir. */
export function massMailingPreview(
  modelName: string,
  id: number,
  mailing: string,
  signal?: AbortSignal,
): Promise<MassMailingPreview> {
  return request<MassMailingPreview>(
    rutaMasivo(modelName, id, mailing),
    signal !== undefined ? { signal } : {},
  );
}

/**
 * Dispara el lote. Contesta 200 aunque haya fallos: el resultado del pedido no
 * es un error del pedido, y un 400 le haria perder al usuario la lista de los
 * que si salieron.
 */
export function massMailingSend(
  modelName: string,
  id: number,
  mailing: string,
  payload: {
    keys?: string[];
    cc?: string[];
    attach?: boolean;
    request_key?: string;
    /**
     * `true` manda de nuevo a quien ya recibió este envío. Sin esto, el backend
     * lo saltea y contesta que ya estaba enviado: reabrir el diálogo y volver a
     * apretar no le manda la expensa dos veces a nadie.
     */
    resend?: boolean;
  },
): Promise<MassMailingResult> {
  return request<MassMailingResult>(rutaMasivo(modelName, id, mailing), {
    method: 'POST',
    body: payload,
  });
}

/**
 * Descarga los PDF del lote en un ZIP. Es el plan de contingencia cuando el
 * despliegue todavia no tiene servidor de correo: en vez de un boton «Enviar»
 * que no manda nada, el administrador se baja las liquidaciones ya nombradas por
 * unidad y las manda por donde pueda.
 */
export function downloadMassMailingPdfs(
  modelName: string,
  id: number,
  mailing: string,
): Promise<DownloadedFile> {
  return descargarArchivo(`${rutaMasivo(modelName, id, mailing)}pdfs/`, `${mailing}.zip`);
}

// ---------------------------------------------------------------------------
// Importacion desde planilla
// ---------------------------------------------------------------------------
/**
 * Sube un archivo a un endpoint del nucleo (multipart) y devuelve su JSON.
 *
 * No pasa por `request<T>` por una razon concreta: ese helper fija
 * `Content-Type: application/json`, y en un multipart el navegador tiene que
 * poner el suyo CON el `boundary` que genera. Fijarlo a mano rompe el parseo del
 * lado del servidor. La cadena de auth —company activa, 401 y un unico
 * refresco— es la misma, reusando `refrescarToken`.
 */
async function subirArchivo<T>(path: string, datos: FormData): Promise<T> {
  const enviar = async (token: string | null): Promise<Response> => {
    const cabeceras: Record<string, string> = { Accept: 'application/json' };
    if (token !== null) cabeceras.Authorization = `Bearer ${token}`;
    if (auth.companyId !== null) cabeceras[COMPANY_HEADER] = String(auth.companyId);
    return fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: cabeceras,
      body: datos,
      credentials: 'same-origin',
    });
  };

  let respuesta: Response;
  try {
    respuesta = await enviar(auth.access);
  } catch (error) {
    throw new ApiError(0, 'No se pudo contactar al servidor. Revisá tu conexion.', {}, error);
  }

  if (respuesta.status === 401 && auth.refresh !== null) {
    respuesta = await enviar(await refrescarToken());
  }

  if (!respuesta.ok) {
    const cuerpo = await leerCuerpo(respuesta);
    if (respuesta.status === 401) notificarExpiracion();
    throw parseErrorBody(respuesta.status, cuerpo);
  }

  return (await leerCuerpo(respuesta)) as T;
}

/**
 * El archivo viaja en los tres pasos porque el backend NO guarda estado entre
 * ellos: el navegador ya tiene el `File` en la mano, y volver a subir 40 kB es
 * mas barato —y muchisimo menos fragil— que un temporal por sesion con su
 * limpieza y su fuga cuando el usuario cierra la pestaña a mitad de camino.
 */
function formDeImportacion(archivo: File, opciones?: ImportOptions): FormData {
  const datos = new FormData();
  datos.append('file', archivo);
  if (opciones !== undefined) datos.append('options', JSON.stringify(opciones));
  return datos;
}

/** Paso 1: lee la planilla y propone el mapeo columna → campo. */
export function importPreview(modelName: string, archivo: File): Promise<ImportPreview> {
  return subirArchivo<ImportPreview>(
    `/import/${encodeURIComponent(modelName)}/preview/`,
    formDeImportacion(archivo),
  );
}

/** Paso 2: procesa TODO sin escribir nada y contesta fila por fila. */
export function importValidate(
  modelName: string,
  archivo: File,
  opciones: ImportOptions,
): Promise<ImportResult> {
  return subirArchivo<ImportResult>(
    `/import/${encodeURIComponent(modelName)}/validate/`,
    formDeImportacion(archivo, opciones),
  );
}

/** Paso 3: la escritura de verdad. Con `atomic` es todo o nada. */
export function importRun(
  modelName: string,
  archivo: File,
  opciones: ImportOptions,
): Promise<ImportResult> {
  return subirArchivo<ImportResult>(
    `/import/${encodeURIComponent(modelName)}/run/`,
    formDeImportacion(archivo, opciones),
  );
}

/** Plantilla vacia del modelo, con los encabezados que el mapeo reconoce. */
export function downloadImportTemplate(
  modelName: string,
  formato: 'csv' | 'xlsx' = 'csv',
): Promise<DownloadedFile> {
  return descargarArchivo(
    `/import/${encodeURIComponent(modelName)}/template/?formato=${formato}`,
    `plantilla.${formato}`,
  );
}

// ---------------------------------------------------------------------------
// Exportacion de listados
// ---------------------------------------------------------------------------
/**
 * El cuerpo de un pedido de exportacion.
 *
 * Va por POST y no por querystring por dos motivos: el dominio de una pantalla
 * con varios filtros puestos supera lo que un servidor acepta en una URL, y los
 * criterios de busqueda de un usuario no tienen por que quedar en el log de
 * accesos del servidor.
 */
function cuerpoDeExportacion(opciones: ExportOptions): Record<string, unknown> {
  const cuerpo: Record<string, unknown> = {};
  if (opciones.formato !== undefined) cuerpo.formato = opciones.formato;
  if (opciones.columns !== undefined) cuerpo.columns = opciones.columns;
  if (opciones.domain !== undefined && opciones.domain.length > 0) cuerpo.domain = opciones.domain;
  if (opciones.order !== undefined) cuerpo.order = opciones.order;
  if (opciones.include_inactive === true) cuerpo.include_inactive = true;
  if (opciones.limit !== undefined) cuerpo.limit = opciones.limit;
  return cuerpo;
}

/**
 * Cuantas filas va a traer el archivo, y con que columnas. NO descarga nada.
 *
 * Es el paso que hace honesta a la exportacion: el usuario ve el numero real
 * antes de generar un solo byte, y sabe si el tope del servidor lo va a cortar.
 */
export function exportPreview(
  modelName: string,
  opciones: ExportOptions = {},
  signal?: AbortSignal,
): Promise<ExportPreview> {
  const init: RequestOptions = { method: 'POST', body: cuerpoDeExportacion(opciones) };
  if (signal !== undefined) init.signal = signal;
  return request<ExportPreview>(`/export/${encodeURIComponent(modelName)}/preview/`, init);
}

/**
 * Baja el listado como CSV o XLSX, con el dominio y el orden de la pantalla.
 *
 * El backend arma el queryset con las MISMAS funciones que el listado, asi que
 * no hay nada que replicar aca: lo que se manda es lo que la pantalla ya tiene
 * puesto.
 */
export function exportRecords(
  modelName: string,
  opciones: ExportOptions = {},
): Promise<DownloadedFile> {
  return descargarArchivo(
    `/export/${encodeURIComponent(modelName)}/`,
    `exportacion.${opciones.formato ?? 'csv'}`,
    { method: 'POST', body: cuerpoDeExportacion(opciones) },
  );
}

/**
 * Baja un REPORTE tabular como XLSX o CSV: el mayor, el balance, el libro de IVA.
 *
 * `POST /api/v1/export/<modelo>/<id>/report/<accion>/`. El backend vuelve a
 * correr la misma accion de servidor que dibujo el cuadro en pantalla —con los
 * mismos parametros y los mismos controles de permiso— y le entrega el resultado
 * al exportador generico. Por eso el archivo no puede diferir de lo que se esta
 * mirando, y por eso esta funcion no sabe de contabilidad: sirve para cualquier
 * reporte de cualquier modulo.
 */
export function exportTabularReport(
  modelName: string,
  id: number,
  actionName: string,
  params: Record<string, JsonValue> = {},
  formato: 'xlsx' | 'csv' = 'xlsx',
): Promise<DownloadedFile> {
  return descargarArchivo(
    `/export/${encodeURIComponent(modelName)}/${id}/report/${encodeURIComponent(actionName)}/`,
    `${actionName}.${formato}`,
    { method: 'POST', body: { formato, params } },
  );
}

// ---------------------------------------------------------------------------
// Adjuntos (`nucleo/core/attachments_api.py`)
// ---------------------------------------------------------------------------
/**
 * Ruta base de los adjuntos de un registro.
 *
 * La ruta nombra SIEMPRE el modelo y el id del registro del que cuelga el
 * archivo, y no solo el id del adjunto. No es una decision de estetica REST: es
 * lo que obliga al servidor a resolver primero el registro con el queryset
 * seguro —permisos, reglas de registro y organizacion— antes de mirar el
 * adjunto. Un id de adjunto adivinado no alcanza.
 */
function rutaAdjuntos(modelName: string, id: number): string {
  return `/attachments/${encodeURIComponent(modelName)}/${id}/`;
}

/** Los adjuntos de un registro, mas si el usuario puede agregar y sacar. */
export function listAttachments(
  modelName: string,
  id: number,
  signal?: AbortSignal,
): Promise<AttachmentList> {
  return request<AttachmentList>(
    rutaAdjuntos(modelName, id),
    signal !== undefined ? { signal } : {},
  );
}

/** Sube un archivo al registro. Exige poder ESCRIBIR el registro. */
export function uploadAttachment(
  modelName: string,
  id: number,
  archivo: File,
  descripcion = '',
): Promise<Attachment> {
  const datos = new FormData();
  datos.append('file', archivo);
  if (descripcion !== '') datos.append('description', descripcion);
  return subirArchivo<Attachment>(rutaAdjuntos(modelName, id), datos);
}

/**
 * Baja el archivo.
 *
 * Va por `fetch` con la cabecera `Authorization` y no por un `<a href>`, porque
 * **no hay una URL que funcione sin la sesion**: el backend resuelve el registro
 * antes de servir un byte. El precio es que el archivo pasa por memoria como
 * Blob; el beneficio es que no existe ningun enlace que se pueda copiar y pegar
 * fuera del sistema.
 */
export function downloadAttachment(
  modelName: string,
  id: number,
  attachmentId: number,
  nombre = 'adjunto',
): Promise<DownloadedFile> {
  return descargarArchivo(`${rutaAdjuntos(modelName, id)}${attachmentId}/download/`, nombre);
}

/** Saca un adjunto: la fila y el archivo. Exige poder ESCRIBIR el registro. */
export function deleteAttachment(
  modelName: string,
  id: number,
  attachmentId: number,
): Promise<void> {
  return request<void>(`${rutaAdjuntos(modelName, id)}${attachmentId}/`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Hilo de seguimiento (`nucleo/core/chatter_api.py`)
// ---------------------------------------------------------------------------
/**
 * Ruta base del hilo de un registro.
 *
 * Igual que los adjuntos, la ruta nombra SIEMPRE el modelo y el id del registro:
 * es lo que obliga al servidor a resolverlo con el queryset seguro antes de
 * devolver una sola linea del hilo. De ahi sale el permiso —«se ve el hilo si se
 * ve el registro»— y por eso no existe un endpoint que liste mensajes sueltos.
 */
function rutaChatter(modelName: string, id: number): string {
  return `/model/${encodeURIComponent(modelName)}/${id}/chatter/`;
}

/** El hilo completo: mensajes, seguidores y pendientes del registro. */
export function fetchChatter(
  modelName: string,
  id: number,
  signal?: AbortSignal,
): Promise<ChatterThread> {
  const init: RequestOptions = {};
  if (signal !== undefined) init.signal = signal;
  return request<ChatterThread>(rutaChatter(modelName, id), init);
}

/**
 * Publica una nota interna. Exige poder ESCRIBIR el registro.
 *
 * El tipo no se manda: el servidor la graba siempre como `note`. Fabricar una
 * linea de auditoria desde el cliente convertiria el hilo en un documento
 * editable, que es justo lo contrario de para que existe.
 */
export function postChatterNote(
  modelName: string,
  id: number,
  body: string,
  subject = '',
): Promise<ChatterMessage> {
  const cuerpo: Record<string, unknown> = { body };
  if (subject !== '') cuerpo.subject = subject;
  return request<ChatterMessage>(rutaChatter(modelName, id), { method: 'POST', body: cuerpo });
}

/** Se suscribe a las novedades del registro. Es idempotente. */
export function followRecord(modelName: string, id: number): Promise<{ following: boolean }> {
  return request<{ following: boolean }>(`${rutaChatter(modelName, id)}follow/`, {
    method: 'POST',
    body: {},
  });
}

/** Da de baja la suscripcion. Es idempotente. */
export function unfollowRecord(modelName: string, id: number): Promise<{ following: boolean }> {
  return request<{ following: boolean }>(`${rutaChatter(modelName, id)}follow/`, {
    method: 'DELETE',
  });
}

/** Agenda un pendiente sobre el registro. Sin responsable queda a nombre de uno. */
export function scheduleActivity(
  modelName: string,
  id: number,
  datos: { summary: string; due_date?: string; assigned_to?: number; activity_type?: string; note?: string },
): Promise<ChatterActivity> {
  return request<ChatterActivity>(`${rutaChatter(modelName, id)}activity/`, {
    method: 'POST',
    body: datos as unknown as Record<string, unknown>,
  });
}

/**
 * Mueve el estado de un pendiente: `done`, `cancelled` o de vuelta a `planned`.
 *
 * La actividad se busca DENTRO del registro y no por su id suelto, asi que el
 * permiso vuelve a ser el del registro.
 */
export function setActivityState(
  modelName: string,
  id: number,
  activityId: number,
  state: string,
): Promise<ChatterActivity> {
  return request<ChatterActivity>(`${rutaChatter(modelName, id)}activity/${activityId}/`, {
    method: 'POST',
    body: { state },
  });
}

/** Borra un pendiente. */
export function deleteActivity(
  modelName: string,
  id: number,
  activityId: number,
): Promise<void> {
  return request<void>(`${rutaChatter(modelName, id)}activity/${activityId}/`, {
    method: 'DELETE',
  });
}

/**
 * Los pendientes del usuario, de todos los modulos a la vez.
 *
 * Es la unica consulta del chatter que no parte de un registro. El servidor
 * filtra igual: un pendiente cuyo registro no se puede leer no viaja, aunque
 * este a nombre de uno.
 */
export function fetchActivities(
  opciones: { scope?: string; state?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<ActivityBoard> {
  const params = new URLSearchParams();
  if (opciones.scope !== undefined) params.set('scope', opciones.scope);
  if (opciones.state !== undefined) params.set('state', opciones.state);
  if (opciones.limit !== undefined) params.set('limit', String(opciones.limit));
  const query = params.toString();
  const init: RequestOptions = {};
  if (signal !== undefined) init.signal = signal;
  return request<ActivityBoard>(`/activities/${query === '' ? '' : `?${query}`}`, init);
}

/** Agrupa y agrega del lado del servidor: alimenta kanban, totales y graficos. */
export function readGroup(
  modelName: string,
  params: ReadGroupParams,
  signal?: AbortSignal,
): Promise<ReadGroupResponse> {
  const cuerpo: Record<string, unknown> = { groupby: params.groupby };
  if (params.domain !== undefined && params.domain.length > 0) cuerpo.domain = params.domain;
  if (params.aggregates !== undefined) cuerpo.aggregates = params.aggregates;
  if (params.include_inactive === true) cuerpo.include_inactive = true;

  const opciones: RequestOptions = { method: 'POST', body: cuerpo };
  if (signal !== undefined) opciones.signal = signal;
  return request<ReadGroupResponse>(`/model/${encodeURIComponent(modelName)}/read_group/`, opciones);
}

/**
 * Busca registros por nombre para poblar un selector many2one.
 * Usa `ilike` sobre el `rec_name` del modelo destino, que es lo que el backend
 * devuelve como `display_name`.
 */
export function nameSearch(
  modelName: string,
  recName: string,
  term: string,
  limit = 20,
  signal?: AbortSignal,
): Promise<ListResponse> {
  const domain: Domain = term === '' ? [] : [[recName, 'ilike', term]];
  return searchRead(modelName, { domain, limit, fields: [recName] }, signal);
}

/**
 * GET y nada mas: un `fetch` de solo lectura contra un endpoint del ERP.
 *
 * Existe por el chat de ayuda del vendedor (`components/guia/`). Ese chat SOLO
 * EXPLICA —no carga, no edita, no emite, no factura— y sus tres endpoints son de
 * lectura pura. Podria haber tres funciones mas en este archivo, una por
 * endpoint; se prefirio una sola puerta porque asi la restriccion queda en el
 * TIPO y no en la disciplina de quien escriba la proxima: desde
 * `components/guia/` no hay forma de armar un POST, porque esta funcion no
 * acepta metodo ni cuerpo.
 *
 * Reusa `request()` a proposito y no arma su propio `fetch`: el refresco de
 * token de un solo vuelo, la cabecera de company y el corte de sesion expirada
 * tienen que valer tambien para el chat. Un segundo camino de HTTP en el cliente
 * es un segundo lugar donde arreglar el mismo bug.
 */
export function fetchReadOnly<T>(
  path: string,
  query?: Record<string, QueryValue>,
  signal?: AbortSignal,
): Promise<T> {
  const opciones: RequestOptions = { method: 'GET' };
  if (query !== undefined) opciones.query = query;
  if (signal !== undefined) opciones.signal = signal;
  return request<T>(path, opciones);
}

export const api = {
  login,
  adoptSession,
  logout,
  fetchSession,
  fetchReadOnly,
  fetchMenus,
  fetchMeta,
  searchRead,
  readRecord,
  createRecord,
  updateRecord,
  deleteRecord,
  archiveRecord,
  callAction,
  callCollectionAction,
  smartButtonTable,
  downloadReport,
  mailPreview,
  mailSend,
  massMailingPreview,
  massMailingSend,
  downloadMassMailingPdfs,
  importPreview,
  importValidate,
  fetchMetaAcciones,
  usuarioDelToken,
  contar,
  importRun,
  downloadImportTemplate,
  exportPreview,
  exportRecords,
  exportTabularReport,
  listAttachments,
  uploadAttachment,
  downloadAttachment,
  deleteAttachment,
  fetchChatter,
  postChatterNote,
  followRecord,
  unfollowRecord,
  scheduleActivity,
  setActivityState,
  deleteActivity,
  fetchActivities,
  readGroup,
  nameSearch,
  getAuthState,
  isAuthenticated,
  setActiveCompanyId,
  getActiveCompanyId,
  clearSession,
  subscribeAuth,
  onSessionExpired,
};
